import { AD_NETWORK, SHORTS_AD_UNITS } from '../../Player/Helpers/adUnits'
import { IStory, IStoryCard } from '../../../types/stories'
import { StoriesEntry, StoriesPlatform } from './storiesAnalytics'

/**
 * 13 Shorts ads ("Advertising Spec - 13 Shorts Stories", phase 1): a video ad
 * after every N cards that start in a session, on the Shorts units. Ad ops
 * set the numbers per platform on the block in the CMS (its `ads`,
 * { desktop, mobileWeb }). Each value the CMS does not send (or sends wrong)
 * takes the spec's default.
 */
export interface IShortsAdConfig {
  /** an ad after every N card starts; null = video ads off */
  every: number | null
  /** seconds from the ad's first frame before it can be passed */
  lockSec: number
  /** how long an ad due on screen may take to start; undefined = the vertical player's */
  loadTimeoutMs?: number
}

export const SHORTS_AD_DEFAULTS = {
  every: 3,
  maxEvery: 13,
  lockSec: 6,
  /** a guard against a typo: below a second no ad could ever start */
  minLoadTimeoutMs: 1000,
  maxLoadTimeoutMs: 30000,
} as const

/** a whole number, sent as a number or as digits; anything else is null */
const wholeNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isInteger(value) ? value : null
  }
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
    return Number(value.trim())
  }
  return null
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/** where each platform's values sit: the CMS names the mobile ones mobileWeb */
const PLATFORM_KEYS = {
  desktop: ['desktop'],
  mobile: ['mobileWeb'],
} as const

const platformValues = (
  raw: unknown,
  platform: 'desktop' | 'mobile'
): Record<string, unknown> => {
  if (!isRecord(raw)) {
    return {}
  }
  const own = PLATFORM_KEYS[platform].map(key => raw[key]).find(isRecord)
  return own ?? {}
}

// each reader: the value, or undefined when it is missing or wrong (the
// default is taken then). For the interval, 0 is a value: video ads off (null)
const readEvery = (value: unknown): number | null | undefined => {
  const n = wholeNumber(value)
  if (n === 0) {
    return null
  }
  return n !== null && n >= 1 && n <= SHORTS_AD_DEFAULTS.maxEvery
    ? n
    : undefined
}
const readLock = (value: unknown) => {
  const n = wholeNumber(value)
  return n !== null && n >= SHORTS_AD_DEFAULTS.lockSec ? n : undefined
}
const readLoadTimeout = (value: unknown) => {
  const n = wholeNumber(value)
  return n !== null &&
    n >= SHORTS_AD_DEFAULTS.minLoadTimeoutMs &&
    n <= SHORTS_AD_DEFAULTS.maxLoadTimeoutMs
    ? n
    : undefined
}

/** one platform's values from the CMS; each key falls back on its own */
export const parseShortsAdConfig = (
  raw: unknown,
  platform: 'desktop' | 'mobile'
): IShortsAdConfig => {
  const cms = platformValues(raw, platform)
  const every = readEvery(cms.videoAdInterval)
  return {
    every: every !== undefined ? every : SHORTS_AD_DEFAULTS.every,
    lockSec: readLock(cms.videoAdSkipLockSec) ?? SHORTS_AD_DEFAULTS.lockSec,
    loadTimeoutMs: readLoadTimeout(cms.videoAdLoadTimeoutMs),
  }
}

/**
 * Phase 2 (SHORTS-40): a GPT display banner over every Nth content card, on
 * the same card count as the video ad. The CMS sends `videoAdOverlayInterval`
 * per platform (the spec calls it overlayInterval): 1 or more (no upper
 * limit), 0 = off. Until the CMS sends it the
 * overlay is off, as the spec asks before phase 2 goes live: no slot is
 * defined and no GPT request is sent.
 */
export const parseShortsOverlayEvery = (
  raw: unknown,
  platform: 'desktop' | 'mobile'
): number | null => {
  const n = wholeNumber(platformValues(raw, platform).videoAdOverlayInterval)
  return n !== null && n >= 1 ? n : null
}

/** the overlay's units: the mobile web one in the app's web view too */
export const SHORTS_OVERLAY_UNITS = {
  desktop: {
    path: `${AD_NETWORK}/Shorts/shorts_desktop_overlay`,
    divId: 'shorts_desktop_overlay',
  },
  mobileWeb: {
    path: `${AD_NETWORK}/Shorts/shorts_mw_overlay`,
    divId: 'shorts_mw_overlay',
  },
} as const

export const shortsOverlayUnit = (platform: StoriesPlatform) =>
  platform === 'desktop'
    ? SHORTS_OVERLAY_UNITS.desktop
    : SHORTS_OVERLAY_UNITS.mobileWeb

/** the card started as the `count`th of the session gets an overlay */
export const overlayDue = (count: number, every: number | null) =>
  !!every && count > 0 && count % every === 0

/**
 * GPT key-values from a cust_params string built with plain separators
 * (dfpUtils.getCustParams, DFPType 'midrollLive'): the video ad's keys, as
 * the spec asks. `tag` keeps its several values as a list.
 */
export const targetingPairs = (custParams: string): [string, string[]][] =>
  custParams
    .split('&')
    .map(pair => {
      const at = pair.indexOf('=')
      if (at < 1) {
        return null
      }
      let value = pair.slice(at + 1)
      try {
        value = decodeURIComponent(value)
      } catch {
        // a stray % in a value: sent as it is
      }
      const key = pair.slice(0, at)
      const values = key === 'tag' ? value.split('&') : [value]
      return [key, values.filter(Boolean)] as [string, string[]]
    })
    .filter((kv): kv is [string, string[]] => !!kv && kv[1].length > 0)

/** the config's platform: the desktop values on desktop, the mobile ones elsewhere */
export const adConfigPlatform = (platform: StoriesPlatform) =>
  platform === 'desktop' ? 'desktop' : 'mobile'

/**
 * The app (its web view): the spec names no unit or platform value for it.
 * Placeholders until ad ops say: the mobile web unit, and the value the
 * vertical player sends from the app.
 */
export const SHORTS_APP_AD_UNIT = SHORTS_AD_UNITS.mobileWeb
export const SHORTS_APP_AD_PLATFORM = 'webview_app'

/** the unit, picked as the analytics pick their platform */
export const shortsAdUnit = (platform: StoriesPlatform) =>
  platform === 'desktop'
    ? SHORTS_AD_UNITS.desktop
    : platform === 'mobile_web'
    ? SHORTS_AD_UNITS.mobileWeb
    : SHORTS_APP_AD_UNIT

/** the full unit name, as the ad events report it */
export const fullAdUnit = (unitPath: string) => `${AD_NETWORK}/${unitPath}`

/** cust_params "platform": desktop or mobile_web on the site, as the spec */
export const shortsAdPlatform = (platform: StoriesPlatform) =>
  platform === 'desktop'
    ? 'desktop'
    : platform === 'mobile_web'
    ? 'mobile_web'
    : SHORTS_APP_AD_PLATFORM

/**
 * The Stories keys added to cust_params on every ad request, from the card
 * the ad follows: GAM reports revenue per episode and position with them
 */
export const shortsAdKeys = (
  story: IStory,
  card: IStoryCard,
  slot: number,
  entry: StoriesEntry
): [string, string][] => [
  ['series', story.seriesName],
  ['episode', String(story.episodeNumber)],
  ['card', String(card.order)],
  ['ad_slot', String(slot)],
  ['entry', entry],
  // the clip itself (SHORTS-30): url_id = video.id, ref = video.name
  ['ref', card.name],
  ['url_id', card.id],
]
